import { PatientWithInsurance } from '@dataTypes/appointment.type'
import type { Patient as PatientRecord } from '@dataTypes/PatientInfo.types'
import { default as axios } from './axiosConfig'
import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'
import { toApiError } from './errors/api-error'

export interface Patient {
  patient_id: string
  first_name: string
  last_name: string
}

export interface GetAllPatientsResponse {
  statusCode: number
  message: string
  data: {
    patients: PatientWithInsurance[]
  }
}

export interface AddNewPatientResponse {
  statusCode: number
  message: string
  data: {
    patient: PatientWithInsurance | null
  }
}

export interface PatientDuplicateCheckResponse {
  statusCode: number
  message: string
  data: {
    duplicate: boolean
  }
}

export interface PatientRequestOptions {
  accessToken?: string
  clinicId?: string
}

export type AddNewPatientPayload = Omit<
  Partial<PatientRecord>,
  'patient_id'
> & {
  user_id?: string
  created_by?: string
  updated_by?: string
}

export interface PatientDuplicateCheckPayload {
  first_name: string
  last_name: string
  date_of_birth: string
  cellphone: string
  email: string
}

export interface UpdatePatientResponse {
  statusCode: number
  message: string
  data: {
    patient: PatientWithInsurance | null
  }
}

export interface DeletePatientResponse {
  statusCode: number
  message: string
  data: string | null
}

export interface PatientSearchResponse {
  statusCode: number
  message: string
  data: {
    patients: PatientWithInsurance[]
  }
}

export interface fetchPatientByIdResponse {
  statusCode: number
  message: string
  data: {
    patient: PatientWithInsurance | null
  }
}

export interface UpdatePatientPhotoByIdResponse {
  statusCode: number
  message: string
  data: {
    patient: PatientWithInsurance | null
  }
}

export interface getSignedPatientPhotoUrlResponse {
  statusCode: number
  message: string
  data: {
    url: string | null
  }
}

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_PATIENT')
const EXTERNAL_LAYER_API = getEnv('REACT_APP_EXTERNAL_LAYER_CLAIMS', '')
const headers = {
  'Content-Type': 'application/json',
}

export const GetAllPatients = async (): Promise<GetAllPatientsResponse> => {
  try {
    const response = await axios.get<GetAllPatientsResponse>(
      `${BUSINESS_LAYER_API}/api/v1/patient`,
      {
        headers,
      },
    )

    return response.data
  } catch (error: unknown) {
    logger.error('Error in GetAllPatients:', error)
    throw toApiError(error, 'Error fetching patients')
  }
}

export const AddNewPatient = async (
  patientData: AddNewPatientPayload,
  options?: PatientRequestOptions,
): Promise<AddNewPatientResponse> => {
  try {
    const response = await axios.post<AddNewPatientResponse>(
      `${BUSINESS_LAYER_API}/api/v1/patient`,
      patientData,
      {
        headers: {
          ...headers,
          ...(options?.accessToken && {
            Authorization: `Bearer ${options.accessToken}`,
          }),
          ...(options?.clinicId && { 'X-Clinic-ID': options.clinicId }),
        },
      },
    )

    return response.data
  } catch (error: unknown) {
    logger.error('Error in AddNewPatient:', error)
    throw toApiError(error, 'Error adding new patient')
  }
}

export const CheckPatientDuplicate = async (
  patientData: PatientDuplicateCheckPayload,
  options: Required<PatientRequestOptions>,
): Promise<PatientDuplicateCheckResponse> => {
  try {
    const response = await axios.post<PatientDuplicateCheckResponse>(
      `${BUSINESS_LAYER_API}/api/v1/patient/duplicate-check`,
      patientData,
      {
        headers: {
          ...headers,
          Authorization: `Bearer ${options.accessToken}`,
          'X-Clinic-ID': options.clinicId,
        },
      },
    )
    return response.data
  } catch (error: unknown) {
    logger.error('Error in CheckPatientDuplicate:', error)
    throw toApiError(error, 'Error checking for an existing patient')
  }
}

export const UpdatePatient = async (
  patientData: Partial<Patient>,
): Promise<UpdatePatientResponse> => {
  try {
    const response = await axios.put<UpdatePatientResponse>(
      `${BUSINESS_LAYER_API}/api/v1/patient/${patientData.patient_id}`,
      { data: patientData },
      {
        headers,
      },
    )

    return response.data
  } catch (error: unknown) {
    logger.error('Error in UpdatePatient:', error)
    throw toApiError(error, 'Error updating patient')
  }
}

export const DeletePatient = async (
  id: string,
): Promise<DeletePatientResponse> => {
  try {
    const response = await axios.delete<DeletePatientResponse>(
      `${BUSINESS_LAYER_API}/api/v1/patient/${id}`,
      {
        headers,
      },
    )

    return response.data
  } catch (error: unknown) {
    logger.error('Error in DeletePatient:', error)
    throw toApiError(error, 'Error deleting patient')
  }
}

export const searchPatientsByName = async (
  name: string,
  exclude_ids: string | undefined = undefined,
): Promise<PatientSearchResponse> => {
  try {
    const { data, status } = await axios.get<PatientSearchResponse>(
      `${BUSINESS_LAYER_API}/api/v1/patient/search?name=${name}${exclude_ids !== undefined ? `&exclude_ids=${exclude_ids}` : ''}`,
      {
        headers,
      },
    )

    if (status !== 200) {
      throw toApiError(
        { isAxiosError: true, response: { status, data } },
        'Failed to fetch patients',
      )
    }

    return data
  } catch (error: unknown) {
    logger.error('Error fetching patients:', error)
    throw toApiError(error, 'Error fetching patients')
  }
}

export const fetchPatientById = async (
  patientId: string,
  excludeInsurancePlans: boolean = false,
): Promise<fetchPatientByIdResponse> => {
  try {
    const { data, status } = await axios.get<fetchPatientByIdResponse>(
      `${BUSINESS_LAYER_API}/api/v1/patient/${patientId}`,
      {
        headers,
        params: {
          ...(excludeInsurancePlans ? { exclude_insurance_plans: true } : {}),
        },
      },
    )

    if (status !== 200) {
      throw toApiError(
        { isAxiosError: true, response: { status, data } },
        'Failed to fetch patient',
      )
    }

    return data
  } catch (error: unknown) {
    logger.error('Error fetching patient:', error)
    throw toApiError(error, 'Error fetching patient')
  }
}

export const updatePatientPhotoById = async (
  patient_id: string,
  file: File,
): Promise<UpdatePatientPhotoByIdResponse> => {
  try {
    const formData = new FormData()
    formData.append('files', file, `${file.name}`)
    const clinicId = localStorage.getItem('clinic_id') || ''
    if (clinicId) {
      formData.append('clinic_id', clinicId)
    }
    const response = await axios.put<UpdatePatientPhotoByIdResponse>(
      `${BUSINESS_LAYER_API}/api/v1/patient/${patient_id}/photo`,
      formData,
      {
        headers: {
          'Content-Type': undefined,
        },
      },
    )

    return response.data
  } catch (error: unknown) {
    logger.error('Error in UpdatePatientPhotoById:', error)
    throw toApiError(error, 'Error updating patient photo')
  }
}

export const getSignedPatientPhotoUrl = async (
  url: string,
): Promise<getSignedPatientPhotoUrlResponse> => {
  try {
    const { data, status } = await axios.post<getSignedPatientPhotoUrlResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/claims/photo-url`,
      { url },
      {
        headers,
      },
    )

    if (status !== 200) {
      throw toApiError(
        { isAxiosError: true, response: { status, data } },
        'Failed to get signed patient photo url',
      )
    }

    return data
  } catch (error: unknown) {
    logger.error('Error getting signed patient photo url:', error)
    throw toApiError(error, 'Error getting signed patient photo url')
  }
}
